import ziperase from '../../services/ziperase';
import { notify } from '../../services/notify';
import errorHandler from '../../services/errorHandler';

const ORGANIZATIONS_PER_PAGE = 10;

const state = {
  organizations: null,
  count: null,
  countries: null,
  selectedOrganization: null,
  organizationFetchParams: null,
  acceptedEula: false,
  serviceNowConnector: null,
}

const mutations = {
  organizations (state, organizations) {
    state.organizations = organizations;
  },
  count (state, count) {
    state.count = count;
  },
  countries (state, countries) {
    state.countries = countries;
  },
  selectOrganization (state, organization) {
    state.selectedOrganization = organization;
  },
  organizationFetchParams(state, params) {
    state.organizationFetchParams = params;
  },
  acceptEula(state, accepted) {
    state.acceptedEula = accepted;
  },
  serviceNowConnector(state, serviceNow) {
    state.serviceNowConnector = serviceNow;
  }
}

const getters = {
  organizations: () => state.organizations,
  count: () => state.count,
  perPage: () => ORGANIZATIONS_PER_PAGE,
  countries: () => state.countries,
  selectedOrganization: () => state.selectedOrganization,
  acceptedEula: () => state.acceptedEula,
  serviceNowConnector: () => state.serviceNowConnector,
}

const actions = {
  async checkEula({ commit }) {
    try {
      const response = await ziperase.checkEula();
      commit('acceptEula', response.data.success);
    } catch (error) {
      notify().error('Failed to check EULA');
    }
  },
  async acceptEula({ commit, dispatch }) {
    try {
      const response = await ziperase.acceptEula();
      commit('acceptEula', response.data.success);
      notify().success(`Success! Accepted End Use License Agreement (EULA).`);
      dispatch('product/fetchProducts', null, { root: true });
    } catch (error) {
      notify().error(errorMessage || `Failed to accept End Use License Agreement (EULA).`);
    }
  },
  async fetchOrganizations({ commit }, params) {
    try {
      commit('organizationFetchParams', params);
      const response = await ziperase.fetchOrganizations(params);
      commit('organizations', response.data.data);
      commit('count', response.data.pagination.count);
    } catch (error) {
      notify().error('Failed to retrieve organizations.');
    }
  },
  async refetchOrganizations({ dispatch, state }) {
    let params = state.organizationFetchParams || {};
    await dispatch('fetchOrganizations', params);
  },
  async createOrganization({}, organization) {
    try {
      await ziperase.createOrganization(organization);
      notify().success(`Success! Created ${organization.name}.`);
      return true;
    } catch (error) {
      let errorMessage = null;
      if (errorHandler.isConflictError(error)) {
        errorMessage = errorHandler.getConflictErrorMessage(error);
      }
      notify().error(errorMessage || `Failed to create ${organization.name}.`);
    }
    return false;
  },
  async updateOrganization({}, organization) {
    try {
      await ziperase.updateOrganization(organization.id, organization);
      notify().success(`Success! Updated ${organization.name}.`);
      return true;
    } catch (error) {
      let errorMessage = null;
      if (errorHandler.isConflictError(error)) {
        errorMessage = errorHandler.getConflictErrorMessage(error);
      }
      notify().error(errorMessage || `Failed to update ${organization.name}.`);
    }
    return false;
  },
  async fetchCountries({ commit }) {
    try {
      const response = await ziperase.fetchCountries();
      commit('countries', response.data);
    } catch (error) {
      notify().error('Failed to fetch countries.');
    }
  },
  async fetchServiceNowConnector({ commit }) {
    try {
      const response = await ziperase.fetchServiceNowConnector();
      commit('serviceNowConnector', response.data);
    } catch (error) {
      if (error.status === 404) {
        commit('serviceNowConnector', null);
      }
      else {
        notify().error('Failed to fetch Service Now Connector.');
      }
    }
  },
  async updateServiceNowConnector({ commit }, serviceNow) {
    try {
      const response = await ziperase.updateServiceNowConnector(serviceNow);
      commit('serviceNowConnector', response.data);
      notify().success(`Success! Updated Service Now Connector.`);
    } catch (error) {
      if (error.response.status === 400) {
        return error.response.data.detail;
      }
      notify().error('Failed to update Service Now Connector.');
    }
    return null;
  },
  async deleteServiceNowConnector({ commit }) {
    try {
      await ziperase.deleteServiceNowConnector();
      commit('serviceNowConnector', null);
    } catch (error) {
      notify().error('Failed to delete Service Now Connector.');
    }
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}
